import { Brand } from '@deltasierra/type-utilities';
import { StringIdentifierSerializers } from './serializers';
import { StringIdentifierSerializer } from './serializers/string-identifier-serializer';

export type StringIdentifier<Parts extends unknown[], Branding extends string> = Brand<string, Branding> & {
    __stringIdentifierParts: Parts;
};

export const StringIdentifier = {
    createStringIdentifier,
    Serializers: StringIdentifierSerializers,
} as const;

const SEPARATOR = '.' as const;

export interface BuiltStringIdentifier<Parts extends unknown[], Branding extends string> {
    /**
     * Creates the string identifier
     *
     * @param parts - The parts tuple
     * @returns The string identifier
     */
    create: (parts: Parts) => StringIdentifier<Parts, Branding>;
    /**
     * Explodes the string identifier to get the parts again
     *
     * @param identifier - The string identifier
     * @returns The parts
     */
    explode: (identifier: StringIdentifier<Parts, Branding>) => Parts;
    /**
     * Converts a raw string to the identifier type
     *
     * @param raw - The raw string value
     * @returns The identifier
     */
    from: (raw: string) => StringIdentifier<Parts, Branding>;
    /**
     * Gets the specific part
     *
     * @param identifier - The identifier
     * @param key - The part to get
     * @returns The part value
     */
    get: <K extends keyof Parts>(identifier: StringIdentifier<Parts, Branding>, key: Exclude<K, keyof []>) => Parts[K];
}

type CreateStringIdentifierArgs<SI extends StringIdentifier<unknown[], string>> = {
    parts: StringIdentifierSerializers<PartsForStringIdentifier<SI>>;
    separator?: string;
    useBase64?: boolean;
};

type PartsForStringIdentifier<SI extends StringIdentifier<unknown[], string>> =
    SI extends StringIdentifier<infer Parts, string> ? Parts : never;

type BrandForStringIdentifier<SI extends StringIdentifier<unknown[], string>> =
    SI extends StringIdentifier<unknown[], infer Brand> ? Brand : never;

type StringIdentifierSerializers<T extends unknown[]> = T extends []
    ? []
    : T extends [infer T0]
      ? [StringIdentifierSerializer<T0>]
      : T extends [infer T0, ...infer Ts]
        ? [StringIdentifierSerializer<T0>, ...StringIdentifierSerializers<Ts>]
        : [StringIdentifierSerializer<T>];

/**
 * Creates a string identifier that can be serialized
 *
 * @param options - Parameter object containing function options
 * @param options.parts - The parts of the identifier
 * @param options.separator - Optional separator string which will act as delimiter, defaults to '.'
 * @param options.useBase64 - Additionally serialize to base64
 * @returns A string identifier object with serialization functions
 */
function createStringIdentifier<SI extends StringIdentifier<unknown[], string>>({
    parts,
    separator = SEPARATOR,
    useBase64 = true,
}: CreateStringIdentifierArgs<SI>): BuiltStringIdentifier<PartsForStringIdentifier<SI>, BrandForStringIdentifier<SI>> {
    return {
        create(serializeParts) {
            const serializedParts = serializeParts.map((part, index) => parts[index].serialize(part as any));

            return serializeStringIdentifierPartsToString(serializedParts, separator, useBase64) as StringIdentifier<
                PartsForStringIdentifier<SI>,
                BrandForStringIdentifier<SI>
            >;
        },
        explode(raw) {
            const result = deserializeStringIdentifierPartsFromString(raw, parts.length, separator, useBase64);
            if (!result) {
                throw new Error(`Could not deserialize string identifier ${raw}`);
            }

            return result.map((part, index) => parts[index].deserialize(part)) as PartsForStringIdentifier<SI>;
        },
        from(val: string) {
            return val as unknown as StringIdentifier<PartsForStringIdentifier<SI>, BrandForStringIdentifier<SI>>;
        },
        get(raw, key) {
            return this.explode(raw)[key];
        },
    };
}

function deserializeStringIdentifierPartsFromString<T extends [...string[]]>(
    raw: string,
    length: number,
    separator: string,
    useBase64: boolean,
): T | null {
    try {
        const decryptedId = useBase64 ? Buffer.from(raw, 'base64').toString() : raw;
        const parts = decryptedId.split(separator);

        if (parts.length !== length) {
            return null;
        }

        return parts as T;
    } catch {
        return null;
    }
}

function serializeStringIdentifierPartsToString<T extends [...string[]]>(
    parts: T,
    separator: string,
    useBase64: boolean,
): string {
    const joined = parts.join(separator);
    if (!useBase64) {
        return joined;
    }
    return Buffer.from(joined).toString('base64');
}
